<template>
  <div class="flex flex-col flex-wrap items-start justify-start mb-3 space-y-4 md:flex-row md:items-center md:space-x-2">
    <div
      id="date-filter"
      class="flex w-64 mt-4 rounded"
    >
      <b-datepicker
        :value="postDateFilter"
        @input="onChanged('postDateFilter', $event)"
        icon="calendar-today"
        size="is-small"
        locale="ES-CL"
        :placeholder="$tc('message.table.header.dateFilter')"
        :range="true"
        data-hj-allow
        :disabled="userDefaultFilters && userDefaultFilters.postDate.length > 0 || loading"
        icon-right="close-circle"
        icon-right-clickable
        @icon-right-click="clearCurrentPostDateFilter"
      />
    </div>

    <div id="accounts-filter">
      <b-dropdown
        :disabled="loading"
      >
        <button
          class="flex items-center px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
          slot="trigger"
        >
          <p class="text-secondary">
            {{ $tc('message.table.header.account', 2) }}
          </p>
          <span>
            {{ allAccountsSelected ?
              $tc('message.table.header.all', 1) : `${activeAccountsLength()} / ${accountsLength}` }}
          </span>
          <img
            :src="require('@assets/images/chevron-down-outline.svg')"
            alt="flecha para abajo"
          >
        </button>

        <b-checkbox
          class="w-full pb-1 pl-2 text-xs border-b text-primary"
          type="is-primary"
          :value="allAccountsSelected"
          @input="selectAllAccounts($event)"
        >
          <p v-if="allAccountsSelected">
            {{ $t('message.accounts.unselectAll') }}
          </p>
          <p v-else>
            {{ $t('message.accounts.selectAll') }}
          </p>
        </b-checkbox>
        <div
          :ref="`account-${accountId}`"
          v-for="(account, accountId, index) in filteredAccounts"
          :key="accountId"
        >
          <b-checkbox
            :value="account.selected"
            @change.native="changeAccountFilter(accountId)"
            type="is-primary"
            class="pl-2"
            :class="[{'pb-5': accountsLength === 1}, 'text-xs', 'text-primary']"
          >
            <b-tooltip
              :position="`${(tooltipIsTop(index))? 'is-top': 'is-bottom'}`"
              v-if="account.fintocRefreshedAt"
            >
              <div
                :ref="`account-${accountId}-info`"
              >
                {{
                  `[${$t(`message.accounts.institution.shortNames.${camelize(account.institution)}`)}]
                ${account.customName ? account.customName :
                  (`${$t(`message.accounts.type.${camelize(account.type)}`)} - XX${account.number.slice(-4)}`)} `
                }}
              </div>
              <template
                v-slot:content
              >
                <div :ref="`account-${accountId}-tooltip-message`">
                  {{ `Actualizado hace ${minutesFromNow(account.fintocRefreshedAt)} minutos`
                  }}
                </div>
              </template>
            </b-tooltip>
            <div
              v-else
              :ref="`account-${accountId}-info`"
            >
              {{
                `[${$t(`message.accounts.institution.shortNames.${camelize(account.institution)}`)}]
                ${account.customName ? account.customName : $t(`message.accounts.type.${camelize(account.type)}`)}
                - XX${account.number.slice(-4)}`
              }}
            </div>
          </b-checkbox>
        </div>
      </b-dropdown>
    </div>

    <div id="movement-type-filter">
      <b-dropdown
        :value="typeFilter"
        @input="onChanged('typeFilter', $event)"
        :disabled="userDefaultFilters && userDefaultFilters.type || loading"
      >
        <button
          class="flex items-center px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
          slot="trigger"
        >
          <p class="text-secondary">
            {{ $t('message.table.header.movements') }}
          </p>
          <span>{{ typeFilter ? movementTypes[typeFilter] : movementTypes.all }}</span>
          <img
            :src="require('@assets/images/chevron-down-outline.svg')"
            alt="flecha para abajo"
          >
        </button>
        <b-dropdown-item
          class="text-xs text-primary"
          value="all"
        >
          {{ movementTypes.all }}
        </b-dropdown-item>
        <b-dropdown-item
          class="text-xs text-primary"
          value="transfer"
        >
          {{ $t('message.table.header.transferences') }}
        </b-dropdown-item>
        <b-dropdown-item
          class="text-xs text-primary"
          value="other"
        >
          {{ $t('message.table.header.others') }}
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div id="sender-institution-filter">
      <b-dropdown
        :value="institutionFilter"
        @input="onChanged('institutionFilter', $event)"
        :disabled="userDefaultFilters && userDefaultFilters.institution || loading"
      >
        <button
          class="flex items-center px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
          slot="trigger"
        >
          <p class="text-secondary">
            {{ $t('message.table.header.originBank') }}
          </p>
          <span>{{ institutionFilter != 'all' ? institutions[institutionFilter] : 'Todos' }}</span>
          <img
            :src="require('@assets/images/chevron-down-outline.svg')"
            alt="flecha para abajo"
          >
        </button>
        <b-dropdown-item
          v-for="(name, key) in institutions"
          :key="key"
          :value="key"
          class="text-xs text-primary"
        >
          {{ name }}
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div id="status-filter">
      <b-dropdown
        :value="statusFilter"
        @input="onChanged('statusFilter', $event)"
        :disabled="userDefaultFilters && userDefaultFilters.status || loading"
      >
        <button
          class="flex items-center px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
          slot="trigger"
        >
          <p class="text-secondary">
            {{ $tc('message.table.header.status', 2) }}
          </p>
          <span>{{ statusFilter ? status[statusFilter] : $tc('message.table.header.all', headerAllIndex) }}</span>
          <img
            :src="require('@assets/images/chevron-down-outline.svg')"
            alt="flecha para abajo"
            class="text-white"
          >
        </button>
        <b-dropdown-item
          class="text-xs text-primary"
          value=""
        >
          {{ $tc('message.table.header.all', headerAllIndex) }}
        </b-dropdown-item>
        <b-dropdown-item
          class="text-xs text-primary"
          value="pending"
        >
          {{ $t('message.table.status.pending') }}
        </b-dropdown-item>
        <b-dropdown-item
          class="text-xs text-primary"
          value="approved"
        >
          {{ $t('message.table.status.approved') }}
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div id="specific-amount-filter">
      <b-dropdown
        :disabled="loading"
      >
        <button
          class="flex items-center px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
          slot="trigger"
        >
          <p class="text-secondary">
            {{ $tc('message.table.header.amount', 1) }}
          </p>
          <span>
            {{ amountFilter ? `$${amountFilter}` : '  ' }}
          </span>
          <img
            :src="require('@assets/images/chevron-down-outline.svg')"
            alt="flecha para abajo"
          >
        </button>

        <div class="flex">
          <input
            class="w-32 py-1 mx-3 text-xs border-b-2 outline-none border-sc-gray-light text-primary focus:border-sc-blue"
            placeholder="Busca un monto exacto"
            :value="amountFilter"
            @input="onChanged('amountFilter', $event.target.value)"
            :disabled="loading"
          >
          <button
            @click="clearCurrentAmountFilter"
            class="px-3 opacity-25 hover:opacity-50"
          >
            <inline-svg
              :src="require('@assets/images/clear-input.svg')"
              class="text-black"
            />
          </button>
        </div>
      </b-dropdown>
    </div>

    <div id="range-amount-filter">
      <b-dropdown
        :disabled="loading"
      >
        <button
          class="flex items-center px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
          slot="trigger"
        >
          <p class="text-secondary">
            {{ $tc('message.table.header.range', 1) }}
          </p>
          <span>
            {{ fromAmountFilter ? `$${fromAmountFilter}` : '  ' }}
            -
            {{ toAmountFilter ? `$${toAmountFilter}` : '  ' }}
          </span>
          <img
            :src="require('@assets/images/chevron-down-outline.svg')"
            alt="flecha para abajo"
          >
        </button>

        <div class="flex">
          <input
            class="w-32 py-1 mx-3 text-sm border-b-2 outline-none border-sc-gray-light text-primary focus:border-sc-blue"
            placeholder="Desde"
            :value="fromAmountFilter"
            @input="onChanged('fromAmountFilter', $event.target.value)"
            :disabled="userDefaultFilters && userDefaultFilters.fromAmount || loading"
          >
          <span class="self-start h-4 text-center">-</span>
          <input
            class="w-32 py-1 mx-3 text-sm border-b-2 outline-none border-sc-gray-light text-primary focus:border-sc-blue"
            placeholder="Hasta"
            :value="toAmountFilter"
            @input="onChanged('toAmountFilter', $event.target.value)"
            :disabled="userDefaultFilters && userDefaultFilters.toAmount || loading"
          >
          <button
            @click="clearCurrentAmountRangeFilter"
            class="px-3 opacity-25 hover:opacity-50"
          >
            <inline-svg
              :src="require('@assets/images/clear-input.svg')"
              class="text-black"
            />
          </button>
        </div>
      </b-dropdown>
    </div>

    <slot name="customFilters" />

    <div class="flex flex-col justify-center mr-4 space-y-4 md:flex-row md:space-y-0">
      <b-switch
        class="md:ml-4"
        :value="inboundFilter"
        @input="onChanged('inboundFilter', $event)"
        size="is-small"
        type="is-primary"
        data-hj-allow
        :disabled="userDefaultFilters && userDefaultFilters.inbound === false || loading"
      >
        <p class="text-sm text-primary">
          {{ $t('message.table.header.inbound') }}
        </p>
      </b-switch>
      <b-switch
        class="md:ml-4"
        size="is-small"
        :value="outboundFilter"
        @input="onChanged('outboundFilter', $event)"
        type="is-primary"
        data-hj-allow
        :disabled="userDefaultFilters && userDefaultFilters.outbound === false || loading"
      >
        <p class="text-sm text-primary">
          {{ $t('message.table.header.outbound') }}
        </p>
      </b-switch>
    </div>
    <slot name="clearFiltersButton" />
  </div>
</template>
<script>
import { camelize } from 'humps';
import pick from 'lodash/pick';

export default {
  data() {
    return {
      institutions: {
        'all': 'Todos los Bancos',
        'cl_banco_de_chile': 'Banco de Chile',
        'cl_banco_santander': 'Banco Santander',
        'cl_banco_bci': 'Banco Bci',
        'cl_banco_estado': 'Banco Estado',
        'cl_banco_itaú': 'Banco Itaú',
      },
      status: {
        'pending': this.$t('message.table.status.pending'),
        'approved': this.$t('message.table.status.approved'),
      },
      movementTypes: {
        'all': this.$tc('message.table.header.all', this.headerAllIndex),
        'transfer': this.$t('message.table.header.transferences'),
        'other': this.$t('message.table.header.others'),
      },
      headerAllIndex: 2,
    };
  },
  props: {
    accountsFilter: {
      type: Object,
      default: () => {},
    },
    postDateFilter: {
      type: Array,
      default: () => [],
    },
    institutionFilter: {
      type: String,
      default: undefined,
    },
    fromAmountFilter: {
      type: Number,
      default: undefined,
    },
    toAmountFilter: {
      type: Number,
      default: undefined,
    },
    amountFilter: {
      type: Number,
      default: undefined,
    },
    inboundFilter: {
      type: Boolean,
      default: true,
    },
    outboundFilter: {
      type: Boolean,
      default: true,
    },
    statusFilter: {
      type: String,
      default: undefined,
    },
    typeFilter: {
      type: String,
      default: undefined,
    },
    userDefaultFilters: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    accountsLength() {
      return Object.keys(this.filteredAccounts).length;
    },
    activeAccounts() {
      return Object.keys(this.filteredAccounts).filter(key => (this.accountsFilter[key].selected));
    },
    allAccountsSelected() {
      return (this.activeAccountsLength() === this.accountsLength) && this.accountsLength > 0;
    },
    filteredAccounts() {
      if (this.userDefaultFilters && this.userDefaultFilters.accounts.length > 0) {
        return pick(this.accountsFilter, this.userDefaultFilters.accounts);
      }

      return this.accountsFilter;
    },
  },
  methods: {
    onChanged(filterName, filterValue) {
      const filterData = { filterName, filterValue };
      this.$emit('filterChanged', filterData);
    },
    selectAllAccounts(value) {
      this.$emit('selectAllAccounts', value);
    },
    activeAccountsLength() {
      return this.activeAccounts[0] ? this.activeAccounts.length : 0;
    },
    changeAccountFilter(accountId) {
      this.$emit('changeAccountFilter', accountId);
    },
    clearCurrentPostDateFilter() {
      this.$emit('clearCurrentPostDateFilter');
    },
    clearCurrentAmountRangeFilter() {
      this.$emit('clearCurrentAmountRangeFilter');
    },
    clearCurrentAmountFilter() {
      this.$emit('clearCurrentAmountFilter');
    },
    minutesFromNow(dateString) {
      const msPerMin = 60000;
      const date = new Date(dateString);
      const now = Date.now();
      const minutes = Math.round((now - date) / msPerMin);

      return minutes;
    },
    tooltipIsTop(index) {
      const last = index === Object.keys(this.accountsFilter).length - 1;

      return last && index > 0;
    },
    camelize,
  },
};
</script>

<style lang="scss">
  .b-radio.radio > .control-label {
    padding-left: 4px;
  }
</style>
